// What to do about it.
//
// The app has been able to say "the Cranbourne line is suspended" since the
// first week. It has never been able to say the next sentence, which is the
// only one anybody standing on a platform actually wants: GET THE GLEN
// WAVERLEY TRAIN TO EAST MALVERN AND WALK THE LAST KILOMETRE. Everything that
// sentence needs was already in the app — the bundled network, the planner,
// the disruption's affected stretch, the live boards — and none of it was
// joined up.
//
// Three ideas and nothing else:
//
//   1. A DISRUPTION IS A SET OF THINGS TO AVOID, accumulated across every
//      serious row that is running right now. Two suspended lines are two
//      route ids; a fault at South Yarra is one stop id that cuts all four
//      lines through it, whatever their own rows say.
//   2. HOME IS A STATION AND A RADIUS. Not an address: a stop id and "I'll
//      walk 20 minutes", which is enough to turn Carnegie into a short list of
//      stations somebody could be dropped at instead, and which the server is
//      never told.
//   3. THE PLANNER ALREADY DOES THE HARD PART. Give it the avoid set and a
//      different destination and it answers the question; this module only
//      decides which questions to ask, and how to rank the answers once the
//      walk at the end is counted.
//
// Pure, with `io` injected, exactly like `journey.js`. No Vue, no store, no
// fetch — which is what lets the whole of it be tested against a fake board
// with a call counter, and the call counter matters: this runs on a phone at a
// barrier and shares one budget with everything else PTV is asked for.

import {
  plan, time, annotate, normaliseAvoid, changeMinutes, CALL_BUDGET, MAX_OPTIONS,
  PLAN_DEADLINE_MS, PLAN_ENOUGH_MS,
} from './journey.js';
import {
  stationById, lineStops, lineName, metresBetween, allStations, affectedStops, knownRouteIds,
} from './network.js';
import { journeySeconds } from './stopping.js';
import { severityOf, delayMinutesFrom, NOT_RUNNING, replacementOf } from './severity.js';
import { radiusMetres, WALK_METRES_PER_MINUTE } from './places.js';

/**
 * How late a train has to be running before a delay is worth rerouting round.
 *
 * Twenty minutes, because that is about the gap at which the alternative stops
 * being silly. Eight minutes late is a train you wait for; twenty is a train
 * you could have beaten by walking to the next line, and offering an
 * alternative to a five-minute delay would teach people to ignore the card.
 */
export const SERIOUS_DELAY_MINUTES = 20;

/** How many stations near a place are worth planning to. */
export const MAX_ARRIVALS = 4;

/**
 * What a minute on foot is worth against a minute on a train.
 *
 * One and a half. A person carrying shopping in the rain does not trade them
 * one for one, and a ranking that did would send somebody on a 25-minute walk
 * to arrive three minutes earlier. It is not so high that it refuses the walk
 * that is the whole point of this file.
 */
export const WALK_WEIGHT = 1.5;

/** What a change is worth, in minutes, when options are compared. */
export const CHANGE_WEIGHT = 3;

/**
 * What a replacement bus does to the time the trains would have taken.
 *
 * Double it, and add ten minutes for every half hour of railway. Buses sit in
 * the traffic the railway was built to avoid and they stop at kerbs rather
 * than platforms, which the doubling covers because it scales with the ride.
 * The ten minutes is the WAIT, and a wait does not happen once.
 *
 * A closure of a few stops is one bus and one wait. A whole line is not: the
 * operator runs it as segment shuttles, and somebody riding the length of it
 * hands over from one shuttle to the next and queues again at each hand-over,
 * behind everybody else off the same bus. Charging the wait once was the
 * estimate quietly claiming that a line run end to end by buses costs the same
 * wait as four stops of it.
 *
 * It is deliberately pessimistic: an estimate that is too gloomy costs
 * somebody a train they would have caught anyway, and one that is too cheerful
 * puts them on a bus that gets them in late for a meeting they were told they
 * would make.
 */
export const BUS_SLACK_FACTOR = 2;
export const BUS_SLACK_MINUTES = 10;

/**
 * How much railway one shuttle covers before somebody changes buses.
 *
 * Half an hour, which is a stretch a single bus is actually run over. Below it
 * nothing moves — every estimate this app has ever quoted for a closed section
 * is the number it was — and above it each further half hour buys another
 * wait, which is the thing a long closure really costs.
 */
export const BUS_STRETCH_MINUTES = 30;

/** The wait, charged once per shuttle rather than once per journey. */
export function busWaitMinutes(timetableMinutes) {
  const minutes = Math.max(0, Number(timetableMinutes) || 0);
  return BUS_SLACK_MINUTES * (1 + Math.floor(minutes / BUS_STRETCH_MINUTES));
}

/**
 * How much sooner a replacement bus has to get you in before it outranks a
 * train.
 *
 * Twenty minutes. The bus is an estimate and the train is a read of a real
 * run, so a dead heat on paper is not a dead heat in life, and anything closer
 * than this is the app trading a number it knows for a number it guessed.
 */
export const BUS_WINS_BY_MINUTES = 20;

/**
 * How many cards this screen shows, whatever the engine found.
 *
 * Four. `MAX_OPTIONS` is what `journey.js` asks a board for and is deliberately
 * a couple more than are wanted, because half of them fall over on a pattern
 * call. This is what somebody reads at a barrier on a phone, and a fifth card
 * below the fold is a card nobody chooses.
 */
export const SHOWN_OPTIONS = 4;

/**
 * How much later an alternative may arrive and still count as the same answer.
 *
 * Ten minutes. The point of a card is a DECISION, and two cards that get off
 * at the same station within ten minutes of each other are not two decisions:
 * they are one route and the next departure on it. Past ten it starts being
 * worth waiting for the better one, which is a decision again.
 */
export const SAME_ANSWER_MINUTES = 10;

/** The longest a push headline may be. Anything more is a truncated alert. */
export const HEADLINE_MAX = 120;

/** Options asked for from one `time()` call: a couple more than are shown. */
export const TIMED_MAX = 6;

/**
 * How many timed options any ONE arrival station may fill.
 *
 * Two. Without a cap the nearest station takes the whole allowance and the
 * card offers five Frankston trains to the same platform, having never asked
 * the Glen Waverley board — which is the answer somebody was looking for. Two
 * is "the next one and the one after", per place.
 */
export const PER_CANDIDATE = 2;

/**
 * How many stations somebody may be asked to WALK TO before boarding.
 *
 * The mirror of `MAX_ARRIVALS`, and smaller. The evening question is "where
 * can a train put me down near home", and a wrong answer there costs a longer
 * walk at the end; the morning question is "where can I walk to and catch
 * something", and a wrong answer there costs a walk taken before anybody finds
 * out. Three, including the station they are standing at.
 */
export const MAX_ORIGINS = 3;

/** What the card says when no live train was read. */
export const ESTIMATE_NOTE = 'Timetable estimate, no live trains read';

// Not 'cancellation': that is ONE run ("the 7:54am Craigieburn to Flinders
// Street service"), and the next train still comes. Read as a blockage, its
// prose named every station from Craigieburn to Flinders Street, which shut the
// city to every line and left "No other way by train" on a normal morning.
const SERIOUS = new Set(['suspended']);
const BUS_WORD = /\b(buses?|coach(es)?)\b/i;

/**
 * "Cranbourne line", struck out before anybody goes looking for stations.
 *
 * `affectedStops` reads the prose for station names, and half the lines in
 * Melbourne are named after their terminus: "Trains suspended, Hurstbridge
 * line" names the station Hurstbridge, which would file a whole-line
 * suspension as a fault at one country platform and leave every other station
 * on the line planned through. "Cranbourne and Pakenham lines suspended" does
 * it twice.
 *
 * So the line names are taken out of the sentence first, conjunctions and all,
 * and what is left is read for stations. A notice that really is about
 * Hurstbridge THE STATION says so somewhere else in the sentence — "lift out
 * of service at Hurstbridge" — and survives this untouched.
 */
let LINE_PHRASE = null;
let LINE_PHRASE_FOR = null;

function linePhrase() {
  const names = knownRouteIds().map((id) => lineName(id)).filter(Boolean);
  const key = names.join('|');
  if (LINE_PHRASE && LINE_PHRASE_FOR === key) return LINE_PHRASE;
  const one = names
    .sort((a, b) => b.length - a.length)
    .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|');
  LINE_PHRASE_FOR = key;
  LINE_PHRASE = new RegExp(`\\b(?:${one})(?:\\s*(?:,|and)\\s*(?:${one}))*\\s+lines?\\b`, 'gi');
  return LINE_PHRASE;
}

/** The stops a row is about, with the line's own name not mistaken for one. */
function spanFromProse(routeId, text) {
  return affectedStops(routeId, String(text).replace(linePhrase(), ' '));
}

/**
 * The stops a disruption row covers on `routeId`, or [] for the whole line.
 *
 * PTV's own stop list first: exact ids, named by the people who closed the
 * station. The prose is read only where they named nothing.
 */
export function spanOf(d, routeId = Number(d?.line ?? d?.routeId)) {
  const named = (d?.stops ?? [])
    .map((s) => Number(s?.stopId ?? s))
    .filter((id) => Number.isFinite(id) && id > 0);
  if (named.length) return named;
  return spanFromProse(routeId, `${d?.title ?? ''} ${d?.description ?? ''}`);
}

// ── Which rows are worth acting on ───────────────────────────────────────────

/**
 * A disruption that changes what somebody does, rather than one that informs.
 *
 * The bar is higher here than the one that buzzes a phone. An alert says "your
 * line is in trouble"; this says "go a different way", and going a different
 * way costs a walk and a change, so a notice about a lift, a works block three
 * weeks out or an eight-minute delay must not trigger it.
 */
export function isSerious(d, now = Date.now()) {
  if (!d) return false;
  // A row whose window has closed is history. PTV leaves them in the feed for
  // a while after the trains are running again.
  const ends = Date.parse(d.endsAt ?? '');
  if (Number.isFinite(ends) && ends < now) return false;

  const severity = severityOf(d, now);
  if (SERIOUS.has(severity)) return true;
  if (severity !== 'delayed') return false;
  const minutes = delayMinutesFrom(`${d.title ?? ''} ${d.description ?? ''}`);
  return Number.isFinite(minutes) && minutes >= SERIOUS_DELAY_MINUTES;
}

/**
 * Everything the planner has to keep away from, across every serious row.
 *
 * `{ routeIds, stopIds, reasons }`, and the split between the two sets is the
 * whole design:
 *
 *   * ROUTE IDS are whole lines that are not running. Avoiding one is avoiding
 *     a line, and every other line is untouched.
 *   * STOP IDS are stretches of railway and single stations, and they cut
 *     EVERY line through them. This is the South Yarra case: the Frankston and
 *     Sandringham lines both ride through that station, PTV files a row for
 *     each, and a reroute that only avoided the stamped lines would still be
 *     sending trains through the closed platform — while a reroute that avoided
 *     those LINES outright would refuse a Frankston train from Carrum to
 *     Mordialloc that never goes near the place.
 *
 * So a station-scoped row adds the stop and NEVER the route: the stop does the
 * cutting, precisely where the fault is, and the line stays usable everywhere
 * else. A section adds both, because a line with a hole in the middle of it is
 * not a line you plan a journey along.
 */
export function blockedBy(disruptions = [], { now = Date.now() } = {}) {
  const routeIds = new Set();
  const stopIds = new Set();
  const reasons = [];

  for (const d of disruptions) {
    if (!isSerious(d, now)) continue;
    const routeId = Number(d.line ?? d.routeId);
    if (!Number.isFinite(routeId)) continue;

    const text = `${d.title ?? ''} ${d.description ?? ''}`;
    const span = spanOf(d, routeId);

    const scope = span.length === 0 ? 'line' : (span.length === 1 ? 'station' : 'section');
    if (scope === 'line') routeIds.add(routeId);
    for (const stopId of span) stopIds.add(Number(stopId));

    reasons.push({
      id: d.id ?? null,
      ptvId: d.ptvId ?? null,
      routeId,
      severity: severityOf(d, now),
      title: String(d.title ?? ''),
      // Carried so the card can say "delays of 25 min" without reading the
      // prose a second time.
      delayMinutes: delayMinutesFrom(text),
      replacementBuses: NOT_RUNNING.test(text) && BUS_WORD.test(text),
      replacement: replacementOf(text),
      scope,
      span: span.map(Number),
    });
  }

  return { routeIds, stopIds, reasons };
}

/** The blocked set as the planner wants it. */
export function avoidFrom(blocked) {
  return normaliseAvoid({
    routeIds: [...(blocked?.routeIds ?? [])],
    stopIds: [...(blocked?.stopIds ?? [])],
  }) ?? { routeIds: new Set(), stopIds: new Set() };
}

/**
 * A blocked set plus what somebody named themselves.
 *
 * Disruptions are facts; these are instructions. The planner does not care
 * which half a route id came from, so they are unioned here, once, and the
 * reasons stay the disruption's — a chip somebody added is not a PTV notice.
 */
export function mergeBlocked(blocked, extra) {
  const routeIds = new Set(blocked?.routeIds ?? []);
  const stopIds = new Set(blocked?.stopIds ?? []);
  for (const id of extra?.routeIds ?? []) {
    const n = Number(id);
    if (Number.isFinite(n) && n > 0) routeIds.add(n);
  }
  for (const id of extra?.stopIds ?? []) {
    const n = Number(id);
    if (Number.isFinite(n) && n > 0) stopIds.add(n);
  }
  return { routeIds, stopIds, reasons: blocked?.reasons ?? [] };
}

// ── Where somebody could be dropped instead ──────────────────────────────────

/** Minutes on foot for a distance, at `WALK_METRES_PER_MINUTE`. */
const walkMinutesFor = (metres) => Math.max(1, Math.round(metres / WALK_METRES_PER_MINUTE));

/** Is every line through this station one the planner has been told to avoid? */
function allLinesAvoided(station, blocked) {
  const lines = (station?.lines ?? []).map(Number).filter((n) => Number.isFinite(n));
  if (!lines.length) return true;
  return lines.every((id) => blocked?.routeIds?.has(id));
}

/**
 * The stations a person could actually get home from, nearest first.
 *
 * Their own station leads the list when it is still usable, because walking
 * nought metres beats every alternative and the ranking should not have to
 * discover that. Everything else is inside the radius they chose, with two
 * exclusions that are the same refusal seen twice: a station that is itself
 * shut, and a station all of whose lines are down — either way no train is
 * going to put anybody there.
 */
export function arrivalStations(place, { blocked = null, max = MAX_ARRIVALS } = {}) {
  const origin = stationById(place?.stopId);
  if (!origin) return [];
  const radius = radiusMetres(place);

  const usable = (station) => !blocked?.stopIds?.has(Number(station.stopId))
    && !allLinesAvoided(station, blocked);

  const out = [];
  if (usable(origin)) out.push({ station: origin, metres: 0, walkMinutes: 0 });

  const near = allStations()
    .filter((s) => Number(s.stopId) !== Number(origin.stopId))
    .map((station) => ({
      station,
      metres: Math.round(metresBetween(origin.lat, origin.lng, station.lat, station.lng)),
    }))
    .filter((row) => row.metres <= radius && usable(row.station))
    .sort((a, b) => a.metres - b.metres)
    .map((row) => ({ ...row, walkMinutes: walkMinutesFor(row.metres) }));

  return [...out, ...near].slice(0, Math.max(0, max));
}

/**
 * The stations somebody could WALK TO and board at, nearest first.
 *
 * The morning case, which is the evening one run backwards. Standing at
 * Carnegie with both its lines suspended, the answer is not a different train
 * at Carnegie — there are none — it is eighteen minutes on foot to East
 * Malvern and the Glen Waverley line from there.
 *
 * Measured from WHERE THE PERSON IS rather than from the place's station,
 * because the two are only the same when they are standing at it. With no
 * place at this end, the only origin is the station they named, walk nought,
 * which is what every caller had before any of this.
 */
export function originStations(fromStopId, { place = null, blocked = null, max = MAX_ORIGINS } = {}) {
  const here = stationById(fromStopId);
  if (!here) return [];

  const near = place ? arrivalStations(place, { blocked, max }) : [];
  const rows = near.length ? near : [{ station: here, metres: 0, walkMinutes: 0 }];

  return rows.map(({ station }) => {
    const metres = Number(station.stopId) === Number(here.stopId)
      ? 0
      : Math.round(metresBetween(here.lat, here.lng, station.lat, station.lng));
    return { station, metres, walkMinutes: metres === 0 ? 0 : walkMinutesFor(metres) };
  }).slice(0, Math.max(0, max));
}

/**
 * Is this person's commute in the way of any of it?
 *
 * Asked of the rules AND of the places, because they answer different halves.
 * A rule knows the path somebody rides and is the better evidence where it
 * exists; a place knows the station somebody starts from on a day they have
 * not written a rule for.
 */
/** The legs of the best plan, as one comparable string, or null. */
function wayOf(from, to, avoid = null) {
  const best = plan(from, to, { avoid })?.itineraries?.[0];
  if (!best) return null;
  return best.legs.map((l) => `${l.kind ?? 'train'}:${l.routeId ?? ''}:${l.fromStopId}>${l.toStopId}`).join('|');
}

/** Does the usual home-to-work trip still plan the same way around `blocked`? */
function sameWayAround(places, blocked) {
  const home = Number(places?.home?.stopId);
  const work = Number(places?.work?.stopId);
  if (!stationById(home) || !stationById(work) || home === work) return false;
  const usual = wayOf(home, work);
  return Boolean(usual) && usual === wayOf(home, work, blocked);
}

export function commuteBlocked({
  rules = [], places = null, disruptions = [], now = Date.now(),
} = {}) {
  const blocked = blockedBy(disruptions, { now });
  const mine = new Set();
  const stops = new Set();

  for (const rule of rules) {
    if (rule?.active === false) continue;
    for (const id of rule?.path?.routes ?? []) mine.add(Number(id));
    for (const id of rule?.path?.stops ?? []) stops.add(Number(id));
    for (const id of [rule?.from, rule?.to]) if (Number.isFinite(Number(id))) stops.add(Number(id));
  }
  for (const kind of ['home', 'work']) {
    const station = stationById(places?.[kind]?.stopId);
    if (!station) continue;
    stops.add(Number(station.stopId));
    for (const id of station.lines ?? []) mine.add(Number(id));
  }

  const lines = [...mine].filter((id) => blocked.routeIds.has(id));
  const cutStops = [...stops].filter((id) => blocked.stopIds.has(id));
  // A closed STRETCH is not the whole line (see `blockedBy`). A rule's path
  // already knows the stations it rides through; with no path to read, the
  // planner is asked whether the usual home-to-work trip still goes the same
  // way with the stretch cut out, and only one place known keeps the old,
  // cautious answer: the stretch is on your line, so it is in your way.
  const hasPath = rules.some((r) => r?.active !== false && r?.path?.stops?.length);
  const sections = (blocked.reasons ?? []).filter((r) => r.scope === 'section' && mine.has(r.routeId));
  if (sections.length && !hasPath && !sameWayAround(places, blocked)) {
    for (const r of sections) if (!lines.includes(r.routeId)) lines.push(r.routeId);
  }
  // A stop that is shut takes its lines down with it, for this person, even
  // where no whole-line row was filed.
  for (const stopId of cutStops) {
    for (const id of stationById(stopId)?.lines ?? []) {
      if (mine.has(Number(id)) && !lines.includes(Number(id))) lines.push(Number(id));
    }
  }

  return {
    blocked,
    hit: lines.length > 0 || cutStops.length > 0,
    lines: lines.sort((a, b) => a - b),
  };
}

// ── Saying why, in one line ──────────────────────────────────────────────────

const NUMBER_WORDS = [
  'No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six',
  'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve',
];

/** "Four", up to twelve, and the numeral after that. */
function numberWord(n) {
  return NUMBER_WORDS[n] ?? String(n);
}

const SEVERITY_WORD = { suspended: 'suspended', cancellation: 'cancelled', delayed: 'delayed' };

/** "Cranbourne", "Cranbourne and Pakenham", "Cranbourne, Pakenham and Frankston". */
function joinNames(names) {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

const stationName = (stopId) => stationById(stopId)?.name ?? '';

/**
 * One line of prose saying what is wrong, in the words a person would use.
 *
 * The grouping is the point. Four rows about South Yarra are one fact — that
 * station is shut — and printing four of them would be the app reciting PTV's
 * filing system at somebody who wants to get home. So station-scoped rows are
 * counted by the LINES THROUGH THE STATION ("Four lines through South Yarra
 * suspended"), whole-line rows of the same severity are named together
 * ("Cranbourne and Pakenham lines suspended"), a section says where the hole
 * is ("Cranbourne line: buses between Caulfield and Dandenong"), and a line
 * being run by buses end to end names the line and says so ("Hurstbridge
 * line: buses replacing trains") rather than reporting the suspension alone
 * and leaving out the one part of it somebody can act on.
 *
 * Clauses are joined with a middle dot, and the FIRST one is what the screens
 * stamp at the top of the card, so the order matters: the fault that cuts the
 * most first.
 */
export function whyLine(blocked) {
  const reasons = blocked?.reasons ?? [];
  if (!reasons.length) return '';

  const clauses = [];

  // Stations, grouped by the station rather than by the row.
  const byStation = new Map();
  for (const r of reasons.filter((x) => x.scope === 'station')) {
    const stopId = Number(r.span[0]);
    const held = byStation.get(stopId) ?? { stopId, severities: [] };
    held.severities.push(r.severity);
    byStation.set(stopId, held);
  }
  for (const held of byStation.values()) {
    const name = stationName(held.stopId);
    if (!name) continue;
    const word = SEVERITY_WORD[held.severities[0]] ?? 'disrupted';
    const count = (stationById(held.stopId)?.lines ?? []).length;
    clauses.push(count > 1
      ? `${numberWord(count)} lines through ${name} ${word}`
      : `${lineName(stationById(held.stopId)?.lines?.[0]) ?? 'The'} line ${word} at ${name}`);
  }

  // Whole lines, grouped by how bad it is. The ones with buses on them are
  // held back for a clause of their own: "Hurstbridge line suspended" is true
  // and leaves out the only part of it somebody can act on.
  for (const severity of ['suspended', 'cancellation']) {
    const names = reasons
      .filter((r) => r.scope === 'line' && r.severity === severity && !r.replacementBuses)
      .map((r) => lineName(r.routeId))
      .filter(Boolean);
    if (!names.length) continue;
    const unique = [...new Set(names)];
    clauses.push(`${joinNames(unique)} ${unique.length > 1 ? 'lines' : 'line'} ${SEVERITY_WORD[severity]}`);
  }

  // Whole lines with buses standing in for them, in the same voice a section
  // uses, and naming the line because there are no two stations to name.
  for (const r of reasons.filter((x) => x.scope === 'line' && x.replacementBuses)) {
    const line = lineName(r.routeId);
    if (!line) continue;
    const vehicle = r.replacement === 'coaches' ? 'coaches' : 'buses';
    clauses.push(`${line} line: ${vehicle} replacing trains`);
  }

  // Sections, which are the only clause that says WHERE.
  for (const r of reasons.filter((x) => x.scope === 'section')) {
    const line = lineName(r.routeId);
    if (!line) continue;
    const from = stationName(r.span[0]);
    const to = stationName(r.span[r.span.length - 1]);
    const where = from && to ? ` between ${from} and ${to}` : '';
    const vehicle = r.replacement === 'coaches' ? 'coaches' : 'buses';
    clauses.push(r.replacementBuses
      ? `${line} line: ${vehicle}${where}`
      : `${line} line ${SEVERITY_WORD[r.severity] ?? 'disrupted'}${where}`);
  }

  // Delays, which name their figure because the figure is the reason.
  for (const r of reasons.filter((x) => x.severity === 'delayed' && x.scope === 'line')) {
    const line = lineName(r.routeId);
    if (!line) continue;
    clauses.push(r.delayMinutes
      ? `${line} line delays of ${r.delayMinutes} min`
      : `${line} line delayed`);
  }

  return clauses.join(' · ');
}

// ── The legs this module builds that the planner never would ─────────────────

/**
 * The last kilometre, on foot.
 *
 * `distanceBased` marks it as a walk measured across the map rather than the
 * five-minute subway between two halves of a city station, because the screens
 * draw the two differently and `zoneSpan` must price neither.
 */
export function walkLeg({
  fromStopId, toStopId, metres, minutes, label = 'home', departAt = null,
} = {}) {
  const mins = Number.isFinite(Number(minutes)) ? Number(minutes) : walkMinutesFor(metres);
  return {
    kind: 'walk',
    mode: 'walk',
    distanceBased: true,
    fromStopId: Number(fromStopId),
    toStopId: Number(toStopId),
    metres: Math.round(Number(metres) || 0),
    minutes: mins,
    label,
    ...(departAt
      ? { departAt, arriveAt: new Date(Date.parse(departAt) + mins * 60_000).toISOString() }
      : {}),
  };
}

/**
 * A replacement bus across the stretch of railway that is shut.
 *
 * `estimated` is not decoration. No board anywhere tells us when one of these
 * leaves: the time on it is the train's time, doubled and padded, and the card
 * has to say so or it is quoting a number it made up.
 *
 * `wholeLine` is how the card knows which sentence to write. Buses across a
 * closed section run between two named stations and the card says so; buses
 * standing in for a whole line run the length of it, and naming the two ends
 * of one person's ride as though they were the closure would be telling them
 * something PTV never said.
 */
export function busLeg({
  fromStopId, toStopId, routeId, minutes, departAt = null, wholeLine = false,
  vehicle = 'bus',
} = {}) {
  const mins = Math.max(1, Math.round(Number(minutes) || 0));
  return {
    kind: 'bus',
    mode: 'replacement-bus',
    fromStopId: Number(fromStopId),
    toStopId: Number(toStopId),
    routeId: Number(routeId),
    minutes: mins,
    estimated: true,
    wholeLine: Boolean(wholeLine),
    vehicle: vehicle === 'coach' ? 'coach' : 'bus',
    departAt,
    arriveAt: departAt
      ? new Date(Date.parse(departAt) + mins * 60_000).toISOString()
      : null,
    stops: [],
  };
}

// ── The timetable, for when there is no live train to read ───────────────────

/**
 * What the timetable says this itinerary would take, with no board asked.
 *
 * The fallback, and an honest one: it is marked `estimated` everywhere it
 * surfaces. It exists because "PTV is not answering" and "there is no way
 * home" are different sentences, and on the night a line goes down the first
 * one is the likely one.
 */
function estimateOption(itinerary, now) {
  let clock = now;
  const legs = [];

  for (const leg of itinerary.legs) {
    if (leg.kind === 'walk') {
      const departAt = new Date(clock).toISOString();
      clock += leg.minutes * 60_000;
      legs.push({ ...leg, departAt, arriveAt: new Date(clock).toISOString(), estimated: true });
      continue;
    }
    if (legs.length) {
      const previous = legs[legs.length - 1];
      clock += changeMinutes(stationById(leg.fromStopId), leg, {
        walked: previous.kind === 'walk',
      }) * 60_000;
    }
    const seconds = journeySeconds({
      routeId: leg.routeId, fromStopId: leg.fromStopId, toStopId: leg.toStopId,
    });
    if (!Number.isFinite(seconds) || seconds <= 0) return null;
    const departAt = new Date(clock).toISOString();
    clock += Math.round(seconds) * 1000;
    legs.push({
      ...leg,
      routeName: lineName(leg.routeId) ?? '',
      destination: '',
      departAt,
      arriveAt: new Date(clock).toISOString(),
      platform: null,
      runRef: null,
      stops: [],
      estimated: true,
    });
  }

  if (!legs.length) return null;
  const leave = legs[0].departAt;
  const arrive = legs[legs.length - 1].arriveAt;
  return {
    leave,
    arrive,
    minutes: Math.round((Date.parse(arrive) - Date.parse(leave)) / 60_000),
    changes: itinerary.changes ?? 0,
    plannedAt: new Date(now).toISOString(),
    legs,
    estimated: true,
  };
}

// ── The answer ───────────────────────────────────────────────────────────────

/** The candidate an option actually ended at, by its last train's stop. */
function landedAt(option, candidates) {
  const last = option.legs[option.legs.length - 1];
  return candidates.find((c) => Number(c.station.stopId) === Number(last?.toStopId)) ?? null;
}

/**
 * A timed option, finished off with the walk and the numbers the card ranks on.
 */
function dress(option, {
  candidates, place, placeKind, now, estimated = false,
  origin = null, fromStopId = null, fromPlaceKind = 'home',
}) {
  const candidate = landedAt(option, candidates);
  if (!candidate) return null;

  // The walk TO the first train, taken before the clock on the trains starts.
  const boarding = origin && origin.walkMinutes > 0 ? origin : null;
  const head = boarding
    ? [walkLeg({
      fromStopId,
      toStopId: boarding.station.stopId,
      metres: boarding.metres,
      minutes: boarding.walkMinutes,
      label: fromPlaceKind,
      departAt: new Date(now).toISOString(),
    })]
    : [];

  // And the walk FROM the last one.
  const walking = Number(candidate.station.stopId) !== Number(place.stopId);
  const tail = walking
    ? [walkLeg({
      fromStopId: candidate.station.stopId,
      toStopId: place.stopId,
      metres: candidate.metres,
      minutes: candidate.walkMinutes,
      label: placeKind,
      departAt: option.arrive,
    })]
    : [];

  const legs = [...head, ...option.legs, ...tail];
  // BOTH ENDS, added up. The ranking asks what the whole journey costs on
  // foot, and a card that counted only the walk at the end would rank a
  // twenty-minute walk to the platform as free.
  const walkMinutes = (boarding?.walkMinutes ?? 0) + (walking ? candidate.walkMinutes : 0);
  const walkMetres = (boarding?.metres ?? 0) + (walking ? candidate.metres : 0);
  const arrivesHomeAt = legs[legs.length - 1].arriveAt ?? option.arrive;
  const leave = legs[0].departAt ?? option.leave;
  const hasBus = legs.some((leg) => leg.kind === 'bus');
  const wasEstimated = estimated || Boolean(option.estimated) || hasBus;

  return {
    ...option,
    legs,
    leave,
    minutes: Math.round((Date.parse(arrivesHomeAt) - Date.parse(leave)) / 60_000),
    kind: hasBus ? 'replacement-bus' : (walkMinutes > 0 ? 'train+walk' : 'train'),
    from: { stopId: Number(legs[0].fromStopId), name: stationById(legs[0].fromStopId)?.name ?? '' },
    boardAt: { stopId: Number(option.legs[0].fromStopId), name: stationById(option.legs[0].fromStopId)?.name ?? '' },
    to: { stopId: Number(candidate.station.stopId), name: candidate.station.name },
    walkMinutes,
    walkMetres,
    arrivesHomeAt,
    estimated: wasEstimated,
    note: wasEstimated ? ESTIMATE_NOTE : null,
    score: Math.round(
      (Date.parse(arrivesHomeAt) - now) / 60_000
      + walkMinutes * WALK_WEIGHT
      + (option.changes ?? 0) * CHANGE_WEIGHT,
    ),
  };
}

/**
 * The same `io`, with every train that leaves before somebody can get there
 * taken off the board.
 *
 * `time()` has no per-itinerary start, and adding one would mean a second
 * clock inside a function whose whole job is the first. Cutting the rows at
 * the `io` boundary says the same thing and says it earlier: a train that
 * cannot be caught never reaches `matchesLeg`, so it never costs the pattern
 * call that would have proved it.
 */
function notBefore(io, readyAt) {
  return {
    departures: async (stopId, routeIds, opts) => {
      const rows = await io.departures(stopId, routeIds, opts);
      return (Array.isArray(rows) ? rows : []).filter((d) => {
        const at = Date.parse(d?.estimatedAt ?? d?.scheduledAt ?? '');
        return !Number.isFinite(at) || at >= readyAt;
      });
    },
    run: (runRef, routeId) => io.run(runRef, routeId),
  };
}

/**
 * The replacement bus, where the person will get on one and PTV says there is
 * one.
 *
 * Built from a plan made WITHOUT the avoid set, because the point of it is to
 * use the line that is down: the shut stretch is cut out of the ridden leg and
 * a bus leg put in its place. Untimed, because no board carries it, and ranked
 * behind every real train unless it wins by `BUS_WINS_BY_MINUTES` — see the
 * note there.
 *
 * A WHOLE LINE COUNTS AS A CLOSED STRETCH TOO, and for a long time it did not.
 * `affectedStops` only reads a span out of the prose where the prose names two
 * stations, so "Buses replace trains on the Hurstbridge line" and "Trains are
 * not running on the Frankston line, buses have been ordered" both come back
 * with an empty span and a scope of `line` — which the old guard threw away.
 * The person had said they would take a bus, the operator had said buses were
 * running, and the app offered trains and walking. So where the closure is the
 * whole line, the shut stretch is simply the part of it this person would have
 * ridden: the ends of their own leg.
 */
function busOptions({ fromStopId, place, blocked, now }) {
  const out = [];
  for (const reason of blocked?.reasons ?? []) {
    if (!reason.replacementBuses) continue;
    const usable = reason.scope === 'line'
      || (reason.scope === 'section' && reason.span.length >= 2);
    if (!usable) continue;

    const planned = plan(fromStopId, place.stopId);
    if (!planned.ok) continue;

    for (const itinerary of planned.itineraries.slice(0, 1)) {
      const estimate = estimateOption(itinerary, now);
      if (!estimate) continue;
      const swapped = swapSpanForBus(estimate, reason, now);
      if (swapped) out.push(swapped);
    }
  }
  return out;
}

/**
 * The ridden leg cut in three: train, bus across the closure, train.
 *
 * ONE FUNCTION FOR BOTH SHAPES OF CLOSURE. A section names the two ends of the
 * hole and the rider may pass through only part of it; a whole line has no
 * ends to name, so the hole is the ride itself and the cut is the whole leg.
 * Everything after that — the timetable look-up, the slack, the clocks pushed
 * along the legs that follow — is the same work, and it is done here once.
 */
function swapSpanForBus(option, reason, now) {
  const wholeLine = reason.scope === 'line';
  const span = reason.span.map(Number);
  const first = span[0];
  const last = span[span.length - 1];
  const stops = lineStops(reason.routeId).map((s) => Number(s.stopId));
  const rank = (id) => stops.indexOf(Number(id));

  const index = option.legs.findIndex((leg) => leg.kind == null
    && Number(leg.routeId) === Number(reason.routeId)
    && (wholeLine || (
      between(rank(leg.fromStopId), rank(leg.toStopId), rank(first))
      && between(rank(leg.fromStopId), rank(leg.toStopId), rank(last))
    )));
  if (index < 0) return null;

  const leg = option.legs[index];
  // In the direction this person is riding, not in the line's own order. With
  // the whole line shut there is nothing to work out: they get on where they
  // would have boarded and off where they would have got off.
  const forwards = rank(leg.fromStopId) < rank(leg.toStopId);
  const enter = wholeLine
    ? Number(leg.fromStopId)
    : (forwards ? (rank(first) < rank(last) ? first : last) : (rank(first) > rank(last) ? first : last));
  const leaveAt = wholeLine ? Number(leg.toStopId) : (enter === first ? last : first);

  const seconds = journeySeconds({ routeId: reason.routeId, fromStopId: enter, toStopId: leaveAt });
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  const timetableMinutes = seconds / 60;
  const busMinutes = Math.round(timetableMinutes * BUS_SLACK_FACTOR)
    + busWaitMinutes(timetableMinutes);

  // EVERYTHING BEFORE THE CLOSURE IS STILL RIDDEN. The cut is made inside one
  // leg; the legs that got the person to it are not touched, and dropping
  // them — as this did until the whole-line case walked straight into it —
  // starts the journey at a station nobody is standing at. The clock picks up
  // at the blocked leg's own departure, which already carries the change time
  // `estimateOption` allowed for.
  const legs = option.legs.slice(0, index);
  let clock = Date.parse(leg.departAt);
  const head = Number(leg.fromStopId) !== Number(enter)
    ? trainPart(leg, leg.fromStopId, enter, clock)
    : null;
  if (head) { legs.push(head); clock = Date.parse(head.arriveAt); }

  const bus = busLeg({
    fromStopId: enter,
    toStopId: leaveAt,
    routeId: reason.routeId,
    minutes: busMinutes,
    departAt: new Date(clock).toISOString(),
    wholeLine,
    vehicle: reason.replacement === 'coaches' ? 'coach' : 'bus',
  });
  legs.push(bus);
  clock = Date.parse(bus.arriveAt);

  const tail = Number(leg.toStopId) !== Number(leaveAt)
    ? trainPart(leg, leaveAt, leg.toStopId, clock)
    : null;
  if (tail) { legs.push(tail); clock = Date.parse(tail.arriveAt); }

  const rest = option.legs.slice(index + 1);
  for (const other of rest) {
    const mins = Math.max(1, Math.round(
      (Date.parse(other.arriveAt) - Date.parse(other.departAt)) / 60_000,
    ));
    const departAt = new Date(clock).toISOString();
    clock += mins * 60_000;
    legs.push({ ...other, departAt, arriveAt: new Date(clock).toISOString(), estimated: true });
  }

  const leave = legs[0].departAt;
  const arrive = legs[legs.length - 1].arriveAt;
  return {
    leave,
    arrive,
    minutes: Math.round((Date.parse(arrive) - Date.parse(leave)) / 60_000),
    changes: option.changes ?? 0,
    plannedAt: new Date(now).toISOString(),
    legs,
    estimated: true,
  };
}

const between = (a, b, x) => x >= Math.min(a, b) && x <= Math.max(a, b) && x >= 0;

/** Half of a ridden leg, timed off the timetable. */
function trainPart(leg, fromStopId, toStopId, clock) {
  const seconds = journeySeconds({ routeId: leg.routeId, fromStopId, toStopId });
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  const departAt = new Date(clock).toISOString();
  return {
    ...leg,
    fromStopId: Number(fromStopId),
    toStopId: Number(toStopId),
    departAt,
    arriveAt: new Date(clock + Math.round(seconds) * 1000).toISOString(),
    stops: [],
    runRef: null,
    estimated: true,
  };
}

/**
 * Every way home that is left, timed and ranked.
 *
 * The shape of it: plan on the GRAPH to each candidate station with the avoid
 * set in force, which costs nothing; rank those proposals on paper (`cost`
 * plus half the walk, so a station a long way off has to be genuinely better
 * on the railway to be worth timing); then ONE `time()` call for the best few,
 * sharing one budget, so that asking about four stations does not cost four
 * plans. The walk is appended afterwards, because it is the same walk whichever
 * train turns up.
 *
 * `{ ok, reason, options, calls, why, candidates }`. An empty `options` with
 * `ok: true` is an answer — no trains in the next two hours — and is not the
 * same as `ok: false`, which means there is no route to plan at all.
 */
export async function rerouteOptions({
  fromStopId, place, placeKind = 'home', blocked = null, travel = null,
  places = null, fromPlace = null, fromPlaceKind = null,
  now = Date.now(), io, budget = CALL_BUDGET, statusFor = null,
  deadlineMs = PLAN_DEADLINE_MS, enoughMs = PLAN_ENOUGH_MS,
} = {}) {
  const why = whyLine(blocked);
  const avoid = avoidFrom(blocked);
  const candidates = arrivalStations(place, { blocked });

  if (!stationById(place?.stopId)) {
    return { ok: false, reason: 'no-place', options: [], calls: 0, why, candidates: [] };
  }

  // THE BUS LANDS WHERE NO TRAIN CAN. `arrivalStations` drops a station whose
  // every line is down, which is right for a train and wrong for the bus that
  // was put on BECAUSE the line is down — and that station is usually home
  // itself. So the place's own station goes back on the list, for the bus's
  // sake alone, with the nought-metre walk it has always had.
  const busCandidates = candidates.some((c) => Number(c.station.stopId) === Number(place.stopId))
    ? candidates
    : [...candidates, { station: stationById(place.stopId), metres: 0, walkMinutes: 0 }];

  // AND IT IS WORKED OUT BEFORE ANYWHERE IS CALLED NOWHERE. "Nowhere to go"
  // used to be decided on the train candidates alone, which is the Hurstbridge
  // evening exactly: the whole line is suspended, every station within the
  // walk is on it, no train can put anybody down — and PTV is running buses
  // the length of it. The screen said there was no way home while the only way
  // home was standing at the kerb. It costs nothing to ask: `busOptions` reads
  // the graph and the timetable and calls no board.
  const buses = travel?.replacementBuses === 'avoid'
    ? []
    : busOptions({ fromStopId, place, blocked, now })
      .map((option) => dress(option, {
        candidates: busCandidates, place, placeKind, now, estimated: true,
      }))
      .filter(Boolean);

  /** No train was found. That is only "nowhere" when there is no bus either. */
  const withoutTrains = (reason) => (buses.length
    ? {
      ok: true,
      reason: null,
      options: annotate(pruneOptions(rankOptions([], buses)), statusFor),
      calls: 0,
      why,
      candidates,
    }
    : { ok: false, reason, options: [], calls: 0, why, candidates });

  if (!candidates.length) return withoutTrains('nowhere');

  // Which of the two places, if either, the person is standing at. Named
  // explicitly by a caller that knows, or worked out from `places` by the
  // common one that does not.
  const here = fromPlace ?? placeAt(fromStopId, places);
  const hereKind = fromPlaceKind ?? kindAt(fromStopId, places);
  const origins = originStations(fromStopId, { place: here, blocked });
  if (!origins.length) return withoutTrains('nowhere');

  // ONE BATCH PER PLACE SOMEBODY COULD BOARD AT, because each one starts its
  // trains at a different minute: the eighteen-minute walk to East Malvern
  // means the 15:59 is catchable and the 15:44 is not, and a single list of
  // itineraries has nowhere to record that.
  const batches = [];
  for (const origin of origins) {
    const proposals = [];
    for (const candidate of candidates) {
      if (Number(candidate.station.stopId) === Number(origin.station.stopId)) continue;
      const planned = plan(origin.station.stopId, candidate.station.stopId, { avoid });
      if (!planned.ok) continue;
      planned.itineraries.forEach((itinerary, rank) => {
        proposals.push({
          itinerary,
          rank,
          // On paper: the railway, plus half of every minute on foot. Half,
          // rather than `WALK_WEIGHT`, because this only decides which
          // proposals are worth a call — the real ranking happens once there
          // are clock times to rank.
          paper: itinerary.cost + (origin.walkMinutes + candidate.walkMinutes) / 2,
        });
      });
    }
    if (!proposals.length) continue;
    // EACH PLACE'S BEST FIRST, then everybody's seconds. `time()` spends its
    // budget down the list, so a list ordered purely by paper score hands the
    // whole of it to the nearest station and never asks about the others.
    proposals.sort((a, b) => a.rank - b.rank || a.paper - b.paper);
    batches.push({
      origin,
      paper: Math.min(...proposals.map((p) => p.paper)),
      itineraries: proposals.slice(0, MAX_OPTIONS).map((p) => p.itinerary),
    });
  }
  if (!batches.length) return withoutTrains('no-route');

  // No walk first: the train from the platform somebody is already standing on
  // is the one they can catch soonest, and it is the one the budget should buy
  // before it buys a proposal that starts with eighteen minutes on foot.
  batches.sort((a, b) => a.origin.walkMinutes - b.origin.walkMinutes || a.paper - b.paper);

  const dressed = [];
  let calls = 0;
  // THE SAME CLOCK THE TRIP PLANNER KEEPS. This used to call `time()` with
  // neither deadline, so a disruption reroute — several origins, six options
  // asked for — kept walking boards until the sixteen-call budget was gone.
  // `plan=1` and the first-train-first read were already here; the silence
  // was not. One clock for the whole screen, remaining time handed to each
  // batch, so the second walk-to station is not a fresh twenty seconds.
  const started = Date.now();
  const deadlineAt = Number.isFinite(deadlineMs) && deadlineMs > 0
    ? started + deadlineMs : null;
  const enoughAt = Number.isFinite(enoughMs) && enoughMs > 0
    ? started + enoughMs : null;
  const remaining = (until) => (
    until == null ? null : Math.max(0, until - Date.now())
  );
  for (const batch of batches) {
    if (calls >= budget) break;
    // `time()` treats a 0 deadline as "no clock", so a batch that starts on
    // the last millisecond must not be handed 0 and then wait forever.
    const left = remaining(deadlineAt);
    if (deadlineAt != null && left === 0) break;
    if (dressed.length && enoughAt != null && remaining(enoughAt) === 0) break;
    const readyAt = now + batch.origin.walkMinutes * 60_000;
    try {
      const result = await time(batch.itineraries, {
        now,
        io: batch.origin.walkMinutes ? notBefore(io, readyAt) : io,
        budget: budget - calls,
        max: TIMED_MAX,
        per: PER_CANDIDATE,
        avoid,
        deadlineMs: left,
        enoughMs: remaining(enoughAt),
      });
      calls += result.calls;
      for (const option of result.options) {
        // Stated again here as well as at the `io`. A board row with no time
        // on it at all is let through above so that a thin fixture still
        // plans; a train whose PATTERN says it left before the walk was done
        // is not a train anybody caught.
        if (Date.parse(option.leave) < readyAt) continue;
        const out = dress(option, {
          candidates, place, placeKind, now, origin: batch.origin, fromStopId, fromPlaceKind: hereKind,
        });
        if (out) dressed.push(out);
      }
    } catch {
      // A dead `io` is not "no way home". It is one fewer source of truth, and
      // the timetable is still standing.
      break;
    }
  }

  if (!dressed.length) {
    for (const batch of batches) {
      const readyAt = now + batch.origin.walkMinutes * 60_000;
      for (const itinerary of batch.itineraries) {
        const estimate = estimateOption(itinerary, readyAt);
        if (!estimate) continue;
        const out = dress(estimate, {
          candidates, place, placeKind, now, estimated: true,
          origin: batch.origin, fromStopId, fromPlaceKind: hereKind,
        });
        if (out) dressed.push(out);
      }
    }
  }

  const options = pruneOptions(rankOptions(dressed, buses));
  return {
    ok: true,
    reason: null,
    options: annotate(options, statusFor),
    calls,
    why,
    candidates,
  };
}

/**
 * The near-duplicates dropped, and the list cut to what a screen can hold.
 *
 * THE RANKING IS RIGHT AND THE LIST WAS STILL WRONG. Five cards came back
 * where three of them rode the same two lines to the same station eight
 * minutes apart, and a 2-change route stood beside a 1-change route to the
 * very same platform. Every one of them scored honestly; none of them was a
 * second answer to the question. What somebody at a barrier is choosing
 * between is WHERE THEY GET OFF, and the list has to read as that.
 *
 * TWO RULES, BOTH ABOUT ONE STATION. Against a rival with NO MORE CHANGES, an
 * option goes when that rival gets in no more than `SAME_ANSWER_MINUTES`
 * later: same trouble, same place, and the ten minutes is the most a later
 * arrival can be worth when nothing else separates them.
 *
 * AN EXTRA CHANGE HAS TO BUY SOMETHING, and what it has to buy is the same ten
 * minutes. An option with more changes survives only where it beats the best
 * simpler option to that station by `SAME_ANSWER_MINUTES` or more. This is the
 * rule the first cut missed and the screen showed the miss: East Malvern at
 * 17:57 with two changes stood beside East Malvern at 18:02 with one, and the
 * first was kept because it arrived EARLIER. Five minutes is not a reason to
 * stand on a third platform. Twenty is, and twenty still gets its card.
 *
 * THE COMPARISON IS AGAINST THE BEST SIMPLER OPTION, not against whatever
 * happens to be above it in the list. Rank order decides which of two equals
 * survives, but "is this extra change worth making" is a question about the
 * simplest way to that station, and the simplest way is not always the
 * best-ranked one.
 *
 * THEN THE CUT IS MADE BY STATION AND NOT BY SCORE. Taking the top four off a
 * ranked list can spend all four on one destination and hide a station that
 * walks half as far; this takes the best card for each distinct station first,
 * in rank order, and only then fills what is left with the next best overall.
 * Four departures of one route is what this whole function exists to stop.
 */
export function pruneOptions(options, { max = SHOWN_OPTIONS } = {}) {
  const rows = Array.isArray(options) ? options.filter(Boolean) : [];
  const keyOf = (o) => Number(o?.to?.stopId ?? NaN);
  const changesOf = (o) => Number(o?.changes ?? 0) || 0;
  const arrivalOf = (o) => Date.parse(o?.arrivesHomeAt ?? o?.arrive ?? '');
  const window = SAME_ANSWER_MINUTES * 60_000;

  // The earliest arrival of every change count at every station, read off the
  // WHOLE list before anything is dropped. An extra change is judged against
  // the best simpler way there is, and the best simpler way may be a card that
  // ranked below this one or was itself pruned a moment later.
  const simplest = new Map();
  for (const option of rows) {
    const station = keyOf(option);
    const at = arrivalOf(option);
    if (!Number.isFinite(station) || !Number.isFinite(at)) continue;
    const changes = changesOf(option);
    const held = simplest.get(station) ?? [];
    if (!(at >= (held[changes] ?? Infinity))) held[changes] = at;
    simplest.set(station, held);
  }

  /** The soonest anything gets to `station` on fewer than `changes` changes. */
  function bestSimplerThan(station, changes) {
    const held = simplest.get(station);
    if (!held) return Infinity;
    let best = Infinity;
    for (let n = 0; n < changes; n += 1) {
      const at = held[n];
      if (Number.isFinite(at) && at < best) best = at;
    }
    return best;
  }

  const kept = [];
  for (const option of rows) {
    const station = keyOf(option);
    // An option with no station to land at cannot be compared with one, and a
    // silently dropped card is worse than a duplicate: it is kept.
    if (!Number.isFinite(station)) { kept.push(option); continue; }

    const mine = arrivalOf(option);
    const changes = changesOf(option);

    // AN EXTRA CHANGE MUST BUY TEN MINUTES. Measured against the best simpler
    // way to this station, whatever its rank, and skipped where there is no
    // simpler way or no clock to compare.
    const simpler = bestSimplerThan(station, changes);
    if (Number.isFinite(simpler) && Number.isFinite(mine) && simpler - mine < window) continue;

    // AND AGAINST ITS EQUALS, the rule that has always been here: a rival
    // already kept, no more changes, getting in no more than ten minutes
    // later. One pass is safe because the list is in rank order, so the
    // survivor of any pair is the better-ranked one.
    const covered = kept.some((k) => {
      if (keyOf(k) !== station) return false;
      if (changesOf(k) > changes) return false;
      const theirs = arrivalOf(k);
      if (!Number.isFinite(theirs) || !Number.isFinite(mine)) return false;
      return theirs - mine <= window;
    });
    if (!covered) kept.push(option);
  }

  const room = Math.max(0, max);
  if (kept.length <= room) return kept;

  const seen = new Set();
  const firstPerStation = [];
  const rest = [];
  for (const option of kept) {
    const station = keyOf(option);
    if (!seen.has(station)) { seen.add(station); firstPerStation.push(option); }
    else rest.push(option);
  }
  // Back into rank order once the places are spoken for: the cut is about
  // WHICH options survive, never about the order they are read in.
  const chosen = new Set([...firstPerStation, ...rest].slice(0, room));
  return kept.filter((o) => chosen.has(o));
}

/**
 * The place whose station somebody is standing at, or null.
 *
 * A caller that knows says so with `fromPlace`; this is for the common one
 * that hands over the whole `places` map and lets the engine work out which
 * end of the commute this is.
 */
function placeAt(fromStopId, places) {
  for (const kind of ['home', 'work']) {
    const held = places?.[kind];
    if (held && Number(held.stopId) === Number(fromStopId)) return held;
  }
  return null;
}

/** Which of the two it was, for the walk leg's label. */
function kindAt(fromStopId, places) {
  for (const kind of ['home', 'work']) {
    if (places?.[kind] && Number(places[kind].stopId) === Number(fromStopId)) return kind;
  }
  return 'home';
}

/**
 * Trains first, on score; a bus only jumps the queue when it wins outright.
 *
 * See `BUS_WINS_BY_MINUTES`. The bus's time is a guess and the train's is a
 * read, so they are not comparable on a dead heat, and a card that put a
 * guessed bus above a real train would be the app being confident about the
 * one thing it does not know.
 */
function rankOptions(trains, buses) {
  const byScore = (a, b) => a.score - b.score;
  const ordered = [...trains].sort(byScore);
  if (!buses.length) return ordered;
  if (!ordered.length) return [...buses].sort(byScore);

  const best = Date.parse(ordered[0].arrivesHomeAt);
  const wins = [];
  const rest = [];
  for (const bus of [...buses].sort(byScore)) {
    const sooner = (best - Date.parse(bus.arrivesHomeAt)) / 60_000;
    (sooner >= BUS_WINS_BY_MINUTES ? wins : rest).push(bus);
  }
  return [...wins, ...ordered, ...rest];
}

// ── The one line the push carries ────────────────────────────────────────────

/**
 * What the notification should say to do, worked out on the phone.
 *
 * THE SERVER IS NEVER TOLD WHERE ANYBODY LIVES. This returns names and route
 * ids and nothing else — no stop id for the place, no walking radius — and the
 * sweep pastes the text into an alert when the lines it names are the lines
 * that went down. That is the whole privacy design: the phone knows the
 * question and the answer, and the server knows only the sentence.
 *
 * Graph-only, deliberately. It is computed when a subscription is written,
 * which may be the middle of the night, and a live board read then would be
 * describing trains that will not be running when the alert fires.
 */
export function fallbackHeadline({ rule, places = null, travel: _travel = null } = {}) {
  const from = Number(rule?.from);
  const to = Number(rule?.to);
  if (!stationById(from) || !stationById(to)) return null;

  // The lines the fault would be on: the ones this rule actually rides, kept
  // to the ones that touch an end of it.
  //
  // BOTH ENDS, not just `from`. A commute is written in both directions, and a
  // rule that reads Flinders Street to Carnegie boards at a station the tunnel
  // lines do not call at — so asking the origin alone what lines it has comes
  // back with nothing for exactly the journey this feature was built for.
  const ridden = (rule?.path?.routes ?? []).map(Number).filter((n) => Number.isFinite(n));
  const endLines = new Set(
    [...(stationById(from)?.lines ?? []), ...(stationById(to)?.lines ?? [])].map(Number),
  );
  const touching = ridden.filter((id) => endLines.has(id));
  const avoids = touching.length ? touching : (ridden.length ? ridden : [...endLines]);
  if (!avoids.length) return null;

  const avoid = { routeIds: new Set(avoids), stopIds: new Set() };
  const origins = endpoints(from, places, avoid);
  const destinations = endpoints(to, places, avoid);
  if (!origins.length || !destinations.length) return null;

  let best = null;
  for (const origin of origins) {
    for (const destination of destinations) {
      if (Number(origin.station.stopId) === Number(destination.station.stopId)) continue;
      const planned = plan(origin.station.stopId, destination.station.stopId, { avoid });
      if (!planned.ok) continue;
      const itinerary = planned.itineraries[0];
      const walks = origin.walkMinutes + destination.walkMinutes;
      const paper = itinerary.cost + walks / 2;
      if (!best || paper < best.paper) best = { paper, itinerary, origin, destination, walks };
    }
  }
  if (!best) return null;

  const lines = [...new Set(
    best.itinerary.legs.filter((leg) => leg.kind == null).map((leg) => lineName(leg.routeId)),
  )].filter(Boolean);
  if (!lines.length) return null;

  const rides = [...new Set(
    best.itinerary.legs.filter((leg) => leg.kind == null).map((leg) => Number(leg.routeId)),
  )];
  // Nothing better than what they already do: every line it would ride is a
  // line the fault is on, so the headline would be advice to catch the train
  // that is not coming.
  if (rides.every((id) => avoids.includes(id))) return null;

  const walkMinutes = best.destination.walkMinutes;
  const where = best.destination.station.name;
  const tail = walkMinutes > 0 ? `, then a ${walkMinutes} min walk` : '';
  const text = trim(`Try the ${joinNames(lines)} line${lines.length > 1 ? 's' : ''} to ${where}${tail}`);

  return { text, avoids: avoids.sort((a, b) => a - b), rides: rides.sort((a, b) => a - b) };
}

/** One end of a rule, plus whatever a place near it can reach on foot. */
function endpoints(stopId, places, avoid) {
  const station = stationById(stopId);
  if (!station) return [];
  const blocked = { routeIds: avoid.routeIds, stopIds: avoid.stopIds };
  const place = ['home', 'work']
    .map((kind) => places?.[kind])
    .find((p) => Number(p?.stopId) === Number(stopId));

  if (!place) {
    return allLinesAvoided(station, blocked)
      ? []
      : [{ station, metres: 0, walkMinutes: 0 }];
  }
  return arrivalStations(place, { blocked });
}

/** `HEADLINE_MAX` characters, cut on a word boundary and never mid-name. */
function trim(text) {
  const line = String(text).replace(/\s+/g, ' ').trim();
  if (line.length <= HEADLINE_MAX) return line;
  const cut = line.slice(0, HEADLINE_MAX);
  const space = cut.lastIndexOf(' ');
  return (space > 0 ? cut.slice(0, space) : cut).trim();
}
